import { useEffect, useReducer, useCallback, useMemo } from 'react';
import { ENV_CONFIG_KEYS } from '@adsk/bim360-matrix-react-api/es/helpers/envConsts';
import useDataApi from '@adsk/bim360-matrix-react-api/es/hooks/useDataApi';
import { useEnvConfig } from '@adsk/bim360-matrix-react-api/es/contexts/env';
import { useProject } from '@adsk/bim360-matrix-react-api';
import { ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA } from '../consts';

const ACTIONS = {
  RESET: 'RESET',
  ADD_RELATIONSHIPS: 'ADD_RELATIONSHIPS',
  REMOVE_RELATIONSHIP: 'REMOVE_RELATIONSHIP',
  EMPTY_RESPONSE: 'EMPTY_RESPONSE',
  SET_ERROR: 'SET_ERROR',
  RETRY: 'RETRY'
};

const initialState = {
  continuationToken: null,
  isLoading: true,
  relationships: [],
  error: null
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return initialState;
    case ACTIONS.ADD_RELATIONSHIPS:
      return {
        ...state,
        isLoading: !!action.payload.continuationToken,
        continuationToken: action.payload.continuationToken,
        relationships: [...state.relationships, ...action.payload.relationships]
      };
    case ACTIONS.REMOVE_RELATIONSHIP:
      return {
        ...state,
        relationships: state.relationships.filter(r => r.id !== action.payload)
      };
    case ACTIONS.EMPTY_RESPONSE:
      return {
        ...state,
        isLoading: false
      };
    case ACTIONS.SET_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload.error
      };
    case ACTIONS.RETRY:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    default:
      throw new Error();
  }
};

const getUrl = (baseUrl, project, type, domain, sourceEntityIds) => {
  if (
    !baseUrl ||
    !project ||
    !project.id ||
    !type ||
    !domain ||
    !sourceEntityIds ||
    !sourceEntityIds.length
  )
    return null;

  return `${baseUrl}/containers/${project.id}/relationships:intersect`;
};

export default (sourceEntityType, sourceEntityIdsArr) => {
  const { type, domain } = ENTITY_TYPE_RELATIONSHIP_SERVICE_METADATA[sourceEntityType];
  const project = useProject();
  const baseUrl = useEnvConfig(ENV_CONFIG_KEYS.RELATIONSHIP_SERVICE);
  const [state, dispatch] = useReducer(dataReducer, initialState);

  const urlWithProject = getUrl(baseUrl, project, type, domain, sourceEntityIdsArr);

  const joinedSourceEntityIds = sourceEntityIdsArr.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sourceEntityIds = useMemo(() => sourceEntityIdsArr, [joinedSourceEntityIds]);
  const requestBody = useMemo(
    () => ({
      entities: sourceEntityIds.map(id => {
        return { id, type, domain };
      })
    }),
    [sourceEntityIds, domain, type]
  );
  const axiosConfig = useMemo(() => ({ data: requestBody, method: 'POST', url: urlWithProject }), [
    requestBody,
    urlWithProject
  ]);

  /** Reset Entities (before fetching a new batch) */
  useEffect(() => {
    if (axiosConfig) {
      dispatch({ type: ACTIONS.RESET });
    }
  }, [axiosConfig]);

  const { error, data, doFetch } = useDataApi(axiosConfig);

  /**
   * Error handling
   */
  useEffect(() => {
    if (error) {
      if (error.response && error.response.status === 404) {
        dispatch({ type: ACTIONS.EMPTY_RESPONSE });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR, payload: { error }});
      }
    }
  }, [error]);

  /**
   * process data
   */
  useEffect(() => {
    if (data && data.relationships) {
      const { relationships } = data;
      const continuationToken = data.page ? data.page.continuationToken : null;
      dispatch({ type: ACTIONS.ADD_RELATIONSHIPS, payload: { relationships, continuationToken }});
    }
  }, [data]);

  /**
   * handle continuationToken
   */
  useEffect(() => {
    if (axiosConfig && state.continuationToken) {
      const urlWithContinuationToken = `${urlWithProject}?continuationToken=${state.continuationToken}`;
      doFetch({ ...axiosConfig, url: urlWithContinuationToken });
    }
  }, [urlWithProject, state.continuationToken, doFetch, axiosConfig]);

  const retry = useCallback(() => {
    const continuationTokenParam = state.continuationToken
      ? `?continuationToken=${state.continuationToken}`
      : '';
    const urlWithContinuationToken = urlWithProject + continuationTokenParam;
    doFetch({ ...axiosConfig, url: urlWithContinuationToken });
    dispatch({ type: ACTIONS.RETRY });
  }, [state.continuationToken, urlWithProject, doFetch, axiosConfig]);

  const addRelationships = relationships => {
    dispatch({ type: ACTIONS.ADD_RELATIONSHIPS, payload: { relationships }});
  };

  const removeRelationship = relationshipId => {
    dispatch({ type: ACTIONS.REMOVE_RELATIONSHIP, payload: relationshipId });
  };

  const relationships = state.isLoading ? [] : state.relationships;

  return {
    isLoading: state.isLoading,
    error: !!state.error,
    relationships,
    retry,
    addRelationships,
    removeRelationship
  };
};
